import { QuestionsTypes, QuestionsDocValue, QuestionValueFromDoc, QuestionsTwilioDocValue } from "./questionsTypes"
import { ChatManager } from '@bt-react/libs'
import { Message } from '@twilio/conversations'
import apiGateway from "../../../services/apiGateway"
import { addEntry } from '../../../lib/audit'
import docUpdateAndLog from "../../../lib/docUpdateAndLog"
import { SyncUiQuestionsState } from "../../reducers/docs/syncUi/types"
import { QandAQuestion } from "../../reducers/questions/types"

const createQuestionsArrayFromDoc = (docValue: QuestionsDocValue) => docValue.conversations.usersConversations.length > 0 ?
  docValue.conversations.usersConversations.flatMap((userConversation) =>
    userConversation.userConversations.map(convoDetails => {
      const user = docValue.users.users.find(userClass => userClass.externalIdV2 === userConversation.userId)
      return {
        id: convoDetails.conversationId,
        isSticky: convoDetails.isPriority,
        isAnswered: convoDetails.isAnswered,
        uniqueConversationName: convoDetails.conversationId,
        user: {
          name: `${user?.firstName} ${user?.lastName}`,
          companyName: user?.companyName,
          authorExternalIdV2: user?.externalIdV2
        }
      }
    })
  ) : []

const setInitialQuestionPriority = (channelId: number, webcastId: number, conversationName: string) => async (dispatch) => {
  const result = await apiGateway.setQuestionAnswered(channelId, webcastId, conversationName, false)
  if (result) dispatch(setQuestionsAnsweredOrPriority(conversationName, false))
}


const tryJoinConversation = async (question: QuestionValueFromDoc, conversationClient: ChatManager, participantUsername: string) => {
  await conversationClient.joinConversation(question.uniqueConversationName)

  const [messages, lastReadMessageIndex] = await Promise.all([
    conversationClient.getMessages(question.uniqueConversationName, 30),
    conversationClient.getNumReadMessages(question.uniqueConversationName, participantUsername)
  ])

  if (messages && messages.length < 1) return

  return {
    id: question.uniqueConversationName,
    text: (messages && messages.length > 0) && messages[0].body,
    created: (messages && messages.length > 0) && messages[0].dateCreated,
    isSticky: question.isSticky,
    isAnswered: question.isAnswered,
    uniqueConversationName: question.uniqueConversationName,
    user: {
      name: question.user.name,
      companyName: question.user.companyName,
      authorExternalIdV2: question.user.authorExternalIdV2
    },
    messages: messages ? messages.map(message => {
      const attributes = message.attributes
      const userName = (attributes as { userName: string }).userName
      const companyName = (attributes as { companyName: string }).companyName
      const userId = ((attributes as { userId: string }).userId)
      return ({
        messageId: message.index,
        messageBody: message.body,
        messageAuthor: userName,
        messageCompanyName: companyName,
        messageUserId: userId,
        created: message.dateCreated
      })
    }) : [],
    numOfMessagesRead: lastReadMessageIndex - 1,
    numberOfRepliesToTheQuestion: messages && messages.length - 1
  }
}

const joinConversationAndGetConversationMessages = async (questionsArrayFromDoc: QuestionValueFromDoc[], participantUsername: string, conversationClient: ChatManager) =>
  await Promise.all(questionsArrayFromDoc.map(async (question) => {

    if (question.uniqueConversationName) {

      try {

       return await tryJoinConversation(question, conversationClient, participantUsername)

      } catch (e) {

        addEntry('PresenterService', 'Q&AConversationConnection', `Could not join the conversation ${question.uniqueConversationName} with user ${participantUsername}. Error: ${e}`)

        /*
          Sometimes a conversation is not joined when the POST request is resolved.
          We set a timeout and retry after 2 seconds to join any conversations that were not joined initially
        */
        return new Promise(resolve => {
          setTimeout(async () => {
            const result = await tryJoinConversation(question, conversationClient, participantUsername)
            resolve(result)
          }, 1000)
        })
      }

    }
  return null
}))

export const setQuestionsInitialState = (docValue: QuestionsTwilioDocValue) => async (dispatch, getState) => {

  const { config: { token, username: participantUsername, channelId, webcastId, bridgeName }, room: { participantInformation } } = getState()

  if (bridgeName !== '') return

  dispatch(listenToQuestionsDocUpdates())
  dispatch({ type: QuestionsTypes.SET_QUESTIONS_REQUEST_LOADING, payload: { loading: true } })

  try {

    await apiGateway.getQuestionsDataAndToken(channelId, webcastId, participantUsername)

  } catch (e) {

    addEntry('PresenterService', 'Q&AConversationConnection', `Could not get conversations and data token with user ${participantUsername}. Error: ${e}`)

  }

  if (docValue.uid === '') {

    return dispatch({ type: QuestionsTypes.SET_QUESTIONS_INITIAL_STATE, payload: { loading: false } })

  }

  let questionsArrayFromDoc: QuestionValueFromDoc[] = []

  try {

    const questionsFromDoc = await apiGateway.getQuestionsDoc(channelId, webcastId)

    if (questionsFromDoc.data.conversations.usersConversations) questionsArrayFromDoc = createQuestionsArrayFromDoc(questionsFromDoc.data)
    else { addEntry('PresenterService', 'Q&AConversationConnection', `Questions doc did not contain usersConversations`) }

  } catch (e) {

    addEntry('PresenterService', 'Q&AConversationConnection', `Could not get the doc from the endpoint for user ${participantUsername} Q&A conversation. Error: ${e}`)
    dispatch({ type: QuestionsTypes.SET_QUESTIONS_REQUEST_LOADING, payload: { loading: false } })
    return

  }


  try {

    const conversationClient = await ChatManager.create(token)

    const result = await joinConversationAndGetConversationMessages(questionsArrayFromDoc, participantUsername, conversationClient)

    const filteredResult = result.filter(res => res !== null && res !== undefined)

    dispatch({ type: QuestionsTypes.SET_QUESTIONS_INITIAL_STATE, payload: { conversationClientState: conversationClient, result: filteredResult}, loading: false })

    /*
      If I'm the only participant in the room loop through each item to
      see if any questions were replied to off screen, set to  unanswered if so.
    */
    if (participantInformation.length < 2) {
      filteredResult.forEach((res: QandAQuestion) => {
        if (!res.isAnswered) return
        if (res.messages.length > 1 && res.numOfMessagesRead < res.numberOfRepliesToTheQuestion) {
          dispatch(setInitialQuestionPriority(channelId, webcastId, res.uniqueConversationName))
        }
      })
    }

  } catch (e) {

    dispatch({ type: QuestionsTypes.SET_QUESTIONS_REQUEST_LOADING, payload: { loading: false } })
    addEntry('PresenterService', 'Q&AConversationConnection', `Could not join any conversations with user ${participantUsername}. Error: ${e}`)

  }

}

export const updateQuestionsArray = (syncUiQuestionsState: SyncUiQuestionsState) => (dispatch, getState) => {
  const { questions: { result } } = getState()

  const updatedQuestionsArray = result.map(obj => {
    if (obj.id !== syncUiQuestionsState.c) return obj

    return {
      ...obj,
      isSticky: syncUiQuestionsState.s,
      isAnswered: syncUiQuestionsState.a
    }
  })

  dispatch({ type: QuestionsTypes.SET_QUESTIONS_PRIORITY_CHANGED, payload: updatedQuestionsArray })
}

export const addedMessageToConversation = (conversationName: string, messageAddedByAudience: boolean) => async (dispatch, getState) => {
  const { questions, config: { username: participantUsername } } = getState()

  const currentQuestion = questions.result.find(questionReply => questionReply.uniqueConversationName === conversationName)

  if (!currentQuestion) return

  try {

    const [messages, numOfMessagesRead] = await Promise.all([
      questions.conversationClientState.getMessages(conversationName, 100),
      questions.conversationClientState.getNumReadMessages(conversationName, participantUsername)
    ])

    const filteredQuestionsArray = questions.result.filter((questionReply => questionReply.uniqueConversationName !== conversationName))

    const result = [...filteredQuestionsArray, {
      ...currentQuestion,
      messages: messages.map((message: Message) => {
        const attributes = message.attributes
        const userName = (attributes as { userName: string }).userName
        const companyName = (attributes as { companyName: string }).companyName
        const userId = (attributes as { userId: string }).userId
        return ({
          messageId: message.index,
          messageBody: message.body,
          messageAuthor: userName,
          messageCompanyName: companyName,
          messageUserId: userId,
          created: message.dateCreated
        })
      }),
      numOfMessagesRead: numOfMessagesRead - 1,
      numberOfRepliesToTheQuestion: messages.length - 1
    }]

    dispatch({ type: QuestionsTypes.SET_QUESTIONS_MESSAGE_ADDED, payload: { conversationClientState: questions.conversationClientState, result }})
    if (messageAddedByAudience) dispatch(setQuestionsAnsweredOrPriority(conversationName, false, true))

  } catch (e) {

    addEntry('PresenterService', 'Q&AConversationConnection', `Could not add message from user ${participantUsername} to Q&A conversation ${conversationName}. Error: ${e}`)

  }

}

export const updateNumberOfMessagesRead = (conversationName: string) => async (dispatch, getState) => {

  const { questions, config: { username: participantUsername } } = getState()

  const currentQuestion = questions.result.find(questionReply => questionReply.uniqueConversationName === conversationName)

  if (!currentQuestion) return

  try {

    const numOfMessagesRead = await questions.conversationClientState.getNumReadMessages(conversationName, participantUsername)

    const filteredQuestionRepliesArray = questions.result.filter((questionReply => questionReply.uniqueConversationName !== conversationName))

    const result = [...filteredQuestionRepliesArray, {
      ...currentQuestion,
      numOfMessagesRead: numOfMessagesRead - 1
    }]

    return dispatch({ type: QuestionsTypes.SET_NUM_OF_QUESTION_REPLIES_READ, payload: { conversationClientState: questions.conversationClientState, result } })
  }

  catch (e) {

    addEntry('PresenterService', 'Q&AConversationConnection', `Could not update number of questions read for user ${participantUsername} Q&A conversation ${conversationName}. Error: ${conversationName}`)
  }

}

export const updateConversations = (questionsDoc) => async (dispatch, getState) => {

  const { config: { username: participantUsername, channelId, webcastId, token }, questions } = getState()

  const questionsDocData = questionsDoc.data

  if (questionsDocData.uid === '') return dispatch({ type: QuestionsTypes.SET_QUESTIONS_INITIAL_STATE, payload: {} })

  let questionsArrayFromDoc: QuestionValueFromDoc[] = []

  try {

    const questionsFromDoc = await apiGateway.getQuestionsDoc(channelId, webcastId)
    questionsArrayFromDoc = createQuestionsArrayFromDoc(questionsFromDoc.data)

  } catch (e) {

    addEntry('PresenterService', 'Q&AConversationConnection', `Could not get the doc on update from the endpoint for user ${participantUsername} Q&A conversation. Error: ${e}`)
    return

  }

  const newQuestionsArray = questionsArrayFromDoc.filter(conversation => {
    if (!questions.result) return true
    return !questions.result.some(item => conversation.uniqueConversationName === item.uniqueConversationName)
  })

  if (newQuestionsArray.length > 0) {

    try {

      const getOrCreateConversationClient = async () => {
        if (!questions.conversationClientState) {
          return await ChatManager.create(token)
        }
        return questions.conversationClientState
      }

      const twilioConversationClient = await getOrCreateConversationClient()

      const result = await joinConversationAndGetConversationMessages(newQuestionsArray, participantUsername, twilioConversationClient)

      return dispatch({ type: QuestionsTypes.SET_QUESTIONS_UPDATED,
        payload: { conversationClientState: twilioConversationClient, result: result.filter(res => res !== null && res !== undefined) } })

    } catch (e) {

      addEntry('PresenterService', 'Q&AConversationConnection', `Could not join the conversation with user ${participantUsername} on update Q&A conversation. Error: ${e}`)

    }

  }

}

export const setQuestionsAnsweredOrPriority = (conversationName: string, isSticky: boolean, messageReply?: boolean) => async (dispatch, getState) => {

  const { docs: { uiDoc }, questions: { result } } = getState()

  const matchingItem = result.find(obj => obj.id === conversationName)

  if (isSticky) {
    await docUpdateAndLog(uiDoc, { questions: {
      c: conversationName,
      s: !matchingItem.isSticky,
      a: matchingItem.isAnswered }
    }, 'uiDoc')
  } else {
    await docUpdateAndLog(uiDoc, { questions: {
      c: conversationName,
      a: messageReply ? false : !matchingItem.isAnswered,
      s: matchingItem.isSticky
    }}, 'uiDoc')
  }

  dispatch({ type: QuestionsTypes.SET_QUESTIONS_SYNC_UPDATING })

}


export const listenToQuestionsDocUpdates = () => (dispatch, getState) => {
  const { docs: { questionsDoc } } = getState()

  dispatch({ type: QuestionsTypes.SET_QUESTIONS_DOC_UPDATE_LISTENER_ADDED })
  questionsDoc.on('updated', () => dispatch(updateConversations(questionsDoc)))
}